<!-- src/components/Message.vue -->
<template>
  <div class="message-component">
    <!-- 消息按钮 -->
    <el-badge :value="unreadCount" :max="99" v-if="unreadCount > 0">
      <el-button type="primary" @click="drawerVisible = true" icon="Bell" circle />
    </el-badge>
    <el-button v-else type="primary" @click="drawerVisible = true" icon="Bell" circle />

    <!-- 消息抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      title="消息通知"
      direction="rtl"
      size="600px"
      @close="handleDrawerClose"
    >
      <div class="message-list">
        <div v-if="messages.length === 0" class="no-messages">
          <el-empty description="暂无消息" />
        </div>
        <el-card
          v-for="(message, index) in messages"
          :key="index"
          class="message-card"
          :class="`message-${message.type || 'info'}`"
        >
          <template #header>
            <div class="message-header">
              <span class="message-title">{{ message.title }}</span>
              <el-tag
                :type="getMessageTypeTag(message.type)"
                size="small"
              >
                {{ getMessageTypeText(message.type) }}
              </el-tag>
            </div>
          </template>
          <div class="message-content">
            {{ message.content }}
          </div>
          <div class="message-time">
            {{ formatMessageTime(message.timestamp) }}
          </div>
        </el-card>
      </div>

      <!-- 底部操作按钮 -->
      <template #footer>
        <div class="drawer-footer">
          <el-button @click="clearMessages">清空消息</el-button>
          <el-button type="primary" @click="markAllRead">全部已读</el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<script>
export default {
  name: 'MessageComponent',
  data() {
    return {
      drawerVisible: false,
      websocket: null,
      messages: [],
      unreadCount: 0,
      reconnectInterval: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      isConnecting: false
    };
  },

  mounted() {
    this.connectWebSocket();
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    // 清理资源
    cleanup() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
      if (this.reconnectInterval) {
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = null;
      }
    },

    // 连接 WebSocket
    connectWebSocket() {
      // 避免重复连接
      if (this.isConnecting || this.websocket?.readyState === WebSocket.OPEN) {
        return;
      }

      this.isConnecting = true;

      // 清除之前的重连定时器
      if (this.reconnectInterval) {
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = null;
      }

      try {
        // 根据环境选择 WebSocket URL
        const wsUri = this.getWebSocketUrl();
        console.log(`正在连接 WebSocket: ${wsUri}`);

        this.websocket = new WebSocket(wsUri);

        this.setupWebSocketEvents();
      } catch (error) {
        console.error('WebSocket连接初始化失败:', error);
        this.handleConnectionError();
      }
    },

    // 获取 WebSocket URL
    getWebSocketUrl() {
      // 生产环境使用 WSS
      if (process.env.NODE_ENV === 'production') {
        return 'wss://admin.58option.cn/ws/messages';
      }

      // 开发环境根据当前页面协议选择

      return 'wss://admin.58option.cn/ws/messages';
    },

    // 设置 WebSocket 事件监听器
    setupWebSocketEvents() {
      if (!this.websocket) return;

      this.websocket.onopen = (event) => {
        console.log('✅ WebSocket连接已建立', event);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
      };

      this.websocket.onmessage = (event) => {
        try {
          const message = this.parseMessage(event.data);
          if (message) {
            this.addMessage(message);
          }
        } catch (error) {
          console.error('❌ 解析消息失败:', error);
        }
      };

      this.websocket.onclose = (event) => {
        console.log('🚪 WebSocket连接已关闭', event);
        this.isConnecting = false;
        this.handleConnectionClose(event);
      };

      this.websocket.onerror = (error) => {
        console.error('❌ WebSocket错误:', error);
        this.isConnecting = false;
        this.handleConnectionError();
      };
    },

    // 解析消息
    parseMessage(data) {
      try {
        const message = typeof data === 'string' ? JSON.parse(data) : data;

        // 确保消息有必要的字段
        return {
          title: message.title || '通知',
          content: message.content || message.message || '无内容',
          type: message.type || 'info',
          timestamp: message.timestamp || new Date().toISOString(),
          id: message.id || Date.now()
        };
      } catch (error) {
        console.error('解析消息数据失败:', error);
        return null;
      }
    },

    // 添加消息
    addMessage(message) {
      // 添加到消息列表开头
      this.messages.unshift({
        ...message,
        read: false
      });

      // 限制消息数量，避免内存泄漏
      if (this.messages.length > 100) {
        this.messages = this.messages.slice(0, 100);
      }

      // 增加未读计数
      if (!this.drawerVisible) {
        this.unreadCount++;
      }

      // 显示浏览器通知（如果用户允许）
      this.showNotification(message);
    },

    // 显示浏览器通知
    showNotification(message) {
      if (Notification.permission === 'granted' && !this.drawerVisible) {
        new Notification(message.title, {
          body: message.content,
          icon: '/favicon.ico'
        });
      }
    },

    // 处理连接关闭
    handleConnectionClose(event) {
      // 正常关闭不需要重连
      if (event.code === 1000) {
        return;
      }

      // 超过最大重连次数
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ 达到最大重连次数，停止重连');
        return;
      }

      // 指数退避重连策略
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      console.log(`🔄 ${this.reconnectAttempts}秒后尝试重新连接...`);
      this.reconnectInterval = setTimeout(() => {
        console.log(`🔄 尝试重新连接 WebSocket (第${this.reconnectAttempts}次)`);
        this.connectWebSocket();
      }, delay);
    },

    // 处理连接错误
    handleConnectionError() {
      console.error('WebSocket连接出错，准备重连...');
      this.handleConnectionClose({ code: 4000 });
    },

    // 获取消息类型对应的标签类型
    getMessageTypeTag(type) {
      const typeMap = {
        'success': 'success',
        'warning': 'warning',
        'error': 'danger',
        'info': 'info'
      };
      return typeMap[type] || 'info';
    },

    // 获取消息类型文本
    getMessageTypeText(type) {
      const textMap = {
        'success': '成功',
        'warning': '警告',
        'error': '错误',
        'info': '信息'
      };
      return textMap[type] || '信息';
    },

    // 格式化消息时间
    formatMessageTime(timestamp) {
      if (!timestamp) return '';

      try {
        const date = new Date(timestamp);
        const now = new Date();

        // 如果是今天，显示时间
        if (date.toDateString() === now.toDateString()) {
          return date.toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
          });
        }

        // 如果是昨天，显示"昨天"
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
          return '昨天 ' + date.toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
          });
        }

        // 其他情况显示日期
        return date.toLocaleString('zh-CN', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return timestamp;
      }
    },

    // 处理抽屉关闭
    handleDrawerClose() {
      this.drawerVisible = false;
      this.markAllRead();
    },

    // 标记所有消息为已读
    markAllRead() {
      this.unreadCount = 0;
      this.messages.forEach(message => {
        message.read = true;
      });
    },

    // 清空所有消息
    clearMessages() {
      this.messages = [];
      this.unreadCount = 0;
    }
  }
};
</script>

<style scoped>
.message-component {
  display: inline-block;
}

.message-list {
  height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 10px;
}

.no-messages {
  text-align: center;
  padding: 40px 20px;
}

.message-card {
  margin-bottom: 15px;
  transition: all 0.3s;
}

.message-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.message-title {
  font-weight: bold;
  font-size: 16px;
}

.message-content {
  margin: 10px 0;
  line-height: 1.5;
}

.message-time {
  text-align: right;
  font-size: 12px;
  color: #909399;
}

.drawer-footer {
  display: flex;
  justify-content: space-between;
}

.message-success {
  border-left: 4px solid #67c23a;
}

.message-warning {
  border-left: 4px solid #e6a23c;
}

.message-error {
  border-left: 4px solid #f56c6c;
}

.message-info {
  border-left: 4px solid #409eff;
}
</style>
